import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { Role, TeamMemberDetail } from "@/api/types";

const teamMembersKey = (teamId: string) => ["team-members", teamId] as const;

export function useTeamMembers(teamId: string) {
  return useQuery({
    queryKey: teamMembersKey(teamId),
    queryFn: () => api.GET<TeamMemberDetail[]>(`/teams/${teamId}/members`),
    enabled: Boolean(teamId),
  });
}

function invalidateMemberCaches(qc: ReturnType<typeof useQueryClient>, teamId: string) {
  void qc.invalidateQueries({ queryKey: teamMembersKey(teamId) });
  void qc.invalidateQueries({ queryKey: ["teams"] });
  void qc.invalidateQueries({ queryKey: ["me"] });
}

export function useAddTeamMember(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      api.POST<TeamMemberDetail>(`/teams/${teamId}/members`, {
        user_id: userId,
        role,
      }),
    onSuccess: () => invalidateMemberCaches(qc, teamId),
  });
}

export function useUpdateTeamMemberRole(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      api.PATCH<TeamMemberDetail>(`/teams/${teamId}/members/${userId}`, {
        role,
      }),
    onSuccess: () => invalidateMemberCaches(qc, teamId),
  });
}

export function useRemoveTeamMember(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.DELETE<void>(`/teams/${teamId}/members/${userId}`),
    onSuccess: () => invalidateMemberCaches(qc, teamId),
  });
}
